import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  HttpClientError,
  IDefaultResponseMessage,
  IQuerySettings,
  QueryKeys,
  getQuerySettings,
} from "@dw/models/shared";
import { useHttpClient, useSnackbar } from "@dw/hooks";
import { IAcceptDanceSchoolUserInviteRequest, IDeclineDanceSchoolUserInviteRequest, IDanceSchoolUser, IInviteDanceSchoolUserRequest, IRemoveDanceSchoolUserAccessRequest, IUpdateDanceSchoolUserRoleRequest } from "@dw/models/danceSchoolUser";

const BASE_URL = "/v1/dance-schools/users";

enum DanceSchoolUsersQueryKeys {
  DanceSchoolUsersList = "DanceSchoolUsersList",
  MyPendingInvites = "MyPendingInvites",
}

/**
 * How often the pending-invite indicator refreshes itself.
 *
 * Longer than the notes poll because an invite is a much rarer event, and nothing in this tab can
 * know one arrived - it is created by somebody else, in another school, on another machine.
 */
export const PENDING_INVITE_POLL_INTERVAL_MS = 5 * 60_000;

/**
 * Schools that have invited this user and are still waiting on an answer.
 *
 * Distinct from `useGetMyDanceSchools`, which is the schools they can already act for: the API
 * filters unaccepted invites out of that list, and it must - it is the same read the authorisation
 * pipeline uses to decide whether a caller may touch a school at all.
 *
 * Polled on the same terms as the notes queries: `refetchInterval` alone would leave the data
 * "fresh" under the app-wide 60-minute `staleTime` and never revalidate on mount, and without
 * `refetchOnWindowFocus` a user returning to the tab waits out the rest of the interval.
 */
export const useGetMyPendingDanceSchoolInvites = (
  settings?: IQuerySettings<IDanceSchoolUser[]>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings({
    refetchInterval: PENDING_INVITE_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: PENDING_INVITE_POLL_INTERVAL_MS / 2,
    // Every signed-in user runs this for the nav indicator, including against an API that predates
    // the route. A 404 there means "no invites", not something to interrupt somebody with.
    autoShowErrorMessages: false,
    ...settings,
  });

  return useQuery<IDanceSchoolUser[], HttpClientError>({
    queryKey: [QueryKeys.DanceSchoolUsers, DanceSchoolUsersQueryKeys.MyPendingInvites],
    queryFn: () => {
      return GET<IDanceSchoolUser[]>(`${BASE_URL}/my-invites`);
    },
    ...querySettings,
  });
};

export const useGetDanceSchoolUsers = (
  settings?: IQuerySettings<IDanceSchoolUser[]>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IDanceSchoolUser[], HttpClientError>({
    queryKey: [QueryKeys.DanceSchoolUsers, DanceSchoolUsersQueryKeys.DanceSchoolUsersList],
    queryFn: () => {
      return GET<IDanceSchoolUser[]>(`${BASE_URL}`);
    },
    ...querySettings,
  });
};

export const useInviteDanceSchoolUserMutation = (
  settings?: IQuerySettings<IDanceSchoolUser, IInviteDanceSchoolUserRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const inviteDanceSchoolUser = async (request: IInviteDanceSchoolUserRequest) => {
    return POST<IDanceSchoolUser>(`${BASE_URL}`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchoolUser, HttpClientError, IInviteDanceSchoolUserRequest>({
    ...querySettings,
    mutationFn: inviteDanceSchoolUser,
    onSuccess: (response, input) => {
      showSnackbar("Successfully sent invite.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchoolUsers,
          DanceSchoolUsersQueryKeys.DanceSchoolUsersList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchoolUsers, response.userId], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useRemoveDanceSchoolUserAccessMutation = (
  settings?: IQuerySettings<IDefaultResponseMessage, IRemoveDanceSchoolUserAccessRequest>
) => {
  const { DELETE } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const removeDanceSchoolUserAccess = async ({ userId }: IRemoveDanceSchoolUserAccessRequest) => {
    return DELETE<IDefaultResponseMessage>(`${BASE_URL}/${userId}`);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, IRemoveDanceSchoolUserAccessRequest>({
    ...querySettings,
    mutationFn: removeDanceSchoolUserAccess,
    onSuccess: (response, input) => {
      showSnackbar("User access has been removed.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchoolUsers,
          DanceSchoolUsersQueryKeys.DanceSchoolUsersList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchoolUsers, input.userId], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolUserRoleMutation = (
  settings?: IQuerySettings<IDanceSchoolUser, IUpdateDanceSchoolUserRoleRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolUserRole = async ({ userId, ...request }: IUpdateDanceSchoolUserRoleRequest) => {
    return PUT<IDanceSchoolUser>(`${BASE_URL}/${userId}/change-role`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchoolUser, HttpClientError, IUpdateDanceSchoolUserRoleRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolUserRole,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated user role");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchoolUsers,
          DanceSchoolUsersQueryKeys.DanceSchoolUsersList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchoolUsers, input.userId], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useAcceptDanceSchoolUserInviteMutation = (
  settings?: IQuerySettings<IDanceSchoolUser, IAcceptDanceSchoolUserInviteRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();

  const inviteDanceSchoolUser = async (request: IAcceptDanceSchoolUserInviteRequest) => {
    return POST<IDanceSchoolUser>(`${BASE_URL}/accept-invite`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchoolUser, HttpClientError, IAcceptDanceSchoolUserInviteRequest>({
    ...querySettings,
    mutationFn: inviteDanceSchoolUser,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchoolUsers,
          DanceSchoolUsersQueryKeys.DanceSchoolUsersList,
        ],
      });
      // Accepting is the one thing that changes which schools this user can act for, so the
      // switcher's list and the pending-invite indicator are both wrong the moment it succeeds.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.DanceSchools] });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchoolUsers,
          DanceSchoolUsersQueryKeys.MyPendingInvites,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchoolUsers, response.userId], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

/**
 * Turn down an invitation.
 *
 * The API deletes the pending row rather than marking it declined - `invitePending: false` is what
 * every membership read takes as "accepted member" - so there is no row left to render and the
 * invalidations below are what removes it from the list.
 */
export const useDeclineDanceSchoolUserInviteMutation = (
  settings?: IQuerySettings<IDefaultResponseMessage, IDeclineDanceSchoolUserInviteRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();

  const declineDanceSchoolUserInvite = async (request: IDeclineDanceSchoolUserInviteRequest) => {
    return POST<IDefaultResponseMessage>(`${BASE_URL}/decline-invite`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, IDeclineDanceSchoolUserInviteRequest>({
    ...querySettings,
    mutationFn: declineDanceSchoolUserInvite,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DanceSchoolUsers, DanceSchoolUsersQueryKeys.MyPendingInvites],
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};
